import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';

const DEFAULT_RECONNECT_DELAYS = [0, 1000, 3000, 5000, 10000, 15000, 30000];

export function useRealtimeFeedHub(hubUrl, {
  enabled = true,
  events = [],
  hubRef: externalHubRef,
  itemEventName,
  onItemReceived,
  onConnectionStateChange,
  onConnected,
  onReconnected,
  reconnectDelays = DEFAULT_RECONNECT_DELAYS,
  logLabel,
} = {}) {
  const internalHubRef = useRef(null);
  const hubRef = externalHubRef ?? internalHubRef;
  const resolvedEvents = [...events];

  if (itemEventName) {
    resolvedEvents.push({
      name: itemEventName,
      handler: onItemReceived,
    });
  }

  const eventNamesKey = resolvedEvents.map(({ name }) => name).join('|');
  const reconnectDelaysKey = reconnectDelays.join(',');
  const handlersRef = useRef({
    onConnectionStateChange,
    onConnected,
    onReconnected,
    resolvedEvents,
  });

  handlersRef.current = {
    onConnectionStateChange,
    onConnected,
    onReconnected,
    resolvedEvents,
  };

  useEffect(() => {
    if (!enabled) {
      hubRef.current = null;
      handlersRef.current.onConnectionStateChange?.('disconnected');
      return () => {};
    }

    let cancelled = false;
    handlersRef.current.onConnectionStateChange?.('connecting');

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: true,
      })
      .withAutomaticReconnect(reconnectDelays)
      .withHubProtocol(new signalR.JsonHubProtocol())
      .build();

    resolvedEvents.forEach(({ name }) => {
      hub.on(name, (...args) => {
        const currentEvent = handlersRef.current.resolvedEvents.find((event) => event.name === name);
        currentEvent?.handler?.(...args);
      });
    });

    hub.onreconnecting(() => {
      handlersRef.current.onConnectionStateChange?.('reconnecting');
    });

    hub.onreconnected(async () => {
      try {
        await handlersRef.current.onReconnected?.(hub);
      } catch (err) {
        console.error(`SignalR reconnect handler error for ${logLabel ?? hubUrl}:`, err);
      }

      if (!cancelled) {
        handlersRef.current.onConnectionStateChange?.('connected');
      }
    });

    hub.onclose(() => {
      if (!cancelled) {
        handlersRef.current.onConnectionStateChange?.('disconnected');
      }
    });

    hubRef.current = hub;

    let startSettled = false;
    const startPromise = hub
      .start()
      .then(async () => {
        try {
          await handlersRef.current.onConnected?.(hub);
        } catch (err) {
          console.error(`SignalR connected handler error for ${logLabel ?? hubUrl}:`, err);
        }

        if (!cancelled) {
          handlersRef.current.onConnectionStateChange?.('connected');
        }
      })
      .catch((err) => {
        const isStopError =
          cancelled ||
          hub.state === signalR.HubConnectionState.Disconnected ||
          err?.message?.includes('stopped during negotiation');

        if (!isStopError) {
          handlersRef.current.onConnectionStateChange?.('disconnected');
          console.error(`SignalR connection error for ${logLabel ?? hubUrl}:`, err);
        }
      })
      .finally(() => {
        startSettled = true;
      });

    return () => {
      cancelled = true;
      const stopHub = () => {
        if (hub.state !== signalR.HubConnectionState.Disconnected) {
          hub.stop().catch(() => {});
        }

        if (hubRef.current === hub) {
          hubRef.current = null;
        }
      };

      if (startSettled) {
        stopHub();
      } else {
        startPromise.then(stopHub).catch(() => {});
      }
    };
  }, [enabled, eventNamesKey, hubRef, hubUrl, logLabel, reconnectDelaysKey]);

  return hubRef.current;
}
